
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function TestimonialsPage() {
  const [logos, setLogos] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    loadLogos();
  }, []);

  const loadLogos = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('logos-partenaires')
        .list('', { limit: 100 });

      if (error) {
        console.error('Erreur lors du chargement des logos:', error);
        setLoading(false);
        return;
      }

      if (data && data.length > 0) {
        const logoUrls = await Promise.all(
          data.map(async (file) => {
            const { data: urlData } = await supabase.storage
              .from('logos-partenaires')
              .createSignedUrl(file.name, 60 * 60 * 24 * 365); // 1 an
            return urlData?.signedUrl || '';
          })
        );
        setLogos(logoUrls.filter(url => url));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des logos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Logos par défaut si aucun logo uploadé
  const defaultLogos = [
    {
      name: "Ville de Nantes",
      image: "https://public.readdy.ai/ai/img_res/d5a5984223eec0f22c962252a7b6e523.jpg"
    },
    {
      name: "Région Pays de la Loire",
      image: "https://readdy.ai/api/search-image?query=Official%20logo%20of%20Pays%20de%20la%20Loire%20region%20France%2C%20regional%20government%20emblem%2C%20modern%20institutional%20branding%2C%20blue%20and%20green%20colors%2C%20clean%20typography%2C%20professional%20design%20on%20white%20background&width=200&height=100&seq=region-logo&orientation=landscape"
    },
    {
      name: "UNESCO",
      image: "https://readdy.ai/api/search-image?query=UNESCO%20official%20logo%2C%20United%20Nations%20Educational%20Scientific%20Cultural%20Organization%20emblem%2C%20international%20organization%20branding%2C%20blue%20and%20white%20colors%2C%20temple%20symbol%2C%20professional%20institutional%20design&width=200&height=100&seq=unesco-logo&orientation=landscape"
    },
    {
      name: "Ministère de la Culture",
      image: "https://readdy.ai/api/search-image?query=French%20Ministry%20of%20Culture%20official%20logo%2C%20Minist%C3%A8re%20de%20la%20Culture%20emblem%2C%20government%20institutional%20branding%2C%20Marianne%20symbol%2C%20blue%20white%20red%20colors%2C%20professional%20design%20on%20white%20background&width=200&height=100&seq=culture-logo&orientation=landscape"
    },
    {
      name: "Centre National de la Musique",
      image: "https://readdy.ai/api/search-image?query=Centre%20National%20de%20la%20Musique%20CNM%20logo%2C%20French%20music%20institution%20emblem%2C%20modern%20cultural%20organization%20branding%2C%20musical%20notes%20symbol%2C%20professional%20design%20on%20white%20background&width=200&height=100&seq=cnm-logo&orientation=landscape"
    },
    {
      name: "SACEM",
      image: "https://readdy.ai/api/search-image?query=SACEM%20official%20logo%2C%20Soci%C3%A9t%C3%A9%20des%20Auteurs%20Compositeurs%20et%20%C3%89diteurs%20de%20Musique%2C%20French%20music%20rights%20organization%20emblem%2C%20professional%20institutional%20branding%2C%20clean%20typography%20on%20white%20background&width=200&height=100&seq=sacem-logo&orientation=landscape"
    }
  ];

  const displayLogos = logos.length > 0 ? logos.map((url, index) => ({
    name: `Partenaire ${index + 1}`,
    image: url
  })) : defaultLogos;

  // Slideshow functionality
  useEffect(() => {
    if (isPlaying && displayLogos.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % displayLogos.length);
      }, 3000); // Change slide every 3 seconds

      return () => clearInterval(interval);
    }
  }, [isPlaying, displayLogos.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % displayLogos.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + displayLogos.length) % displayLogos.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-orange-50 to-red-50 py-20">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Ils nous font confiance
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez les institutions et partenaires qui nous accompagnent dans notre mission 
              de promotion de la culture africaine et de la diversité artistique.
            </p>
          </div>
        </section>

        {/* Logos des Partenaires - Diaporama */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Nos Partenaires
              </h2>
              <p className="text-lg text-gray-600">
                Des collaborations solides avec les acteurs majeurs de la culture
              </p>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <i className="ri-loader-4-line text-4xl text-orange-600 animate-spin"></i>
                <p className="text-gray-600 mt-4">Chargement des logos...</p>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto">
                {/* Diaporama des logos */}
                <div className="relative bg-gray-50 rounded-2xl overflow-hidden">
                  {/* Logo principal */}
                  <div className="relative h-80 flex items-center justify-center p-8">
                    <img
                      src={displayLogos[currentSlide]?.image}
                      alt={displayLogos[currentSlide]?.name}
                      className="max-w-full max-h-full object-contain"
                    />
                    
                    {/* Navigation Arrows */}
                    <button
                      onClick={prevSlide}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center text-gray-700 transition-all cursor-pointer shadow-lg"
                    >
                      <i className="ri-arrow-left-line text-xl"></i>
                    </button>
                    <button
                      onClick={nextSlide}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center text-gray-700 transition-all cursor-pointer shadow-lg"
                    >
                      <i className="ri-arrow-right-line text-xl"></i>
                    </button>

                    {/* Play/Pause Button */}
                    <button
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="absolute top-4 right-4 w-12 h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center text-gray-700 transition-all cursor-pointer shadow-lg"
                    >
                      <i className={`${isPlaying ? 'ri-pause-line' : 'ri-play-line'} text-xl`}></i>
                    </button>
                  </div>

                  {/* Slide Counter */}
                  <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {currentSlide + 1} / {displayLogos.length}
                  </div>
                </div>

                {/* Thumbnail Navigation */}
                <div className="mt-6 flex justify-center">
                  <div className="flex gap-2 overflow-x-auto pb-2 max-w-full">
                    {displayLogos.map((logo, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`flex-shrink-0 w-20 h-12 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                          index === currentSlide
                            ? 'border-orange-500 opacity-100'
                            : 'border-gray-300 opacity-60 hover:opacity-80'
                        }`}
                      >
                        <img
                          src={logo.image}
                          alt={logo.name}
                          className="w-full h-full object-contain bg-white p-1"
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Progress Bar */}
                {isPlaying && (
                  <div className="mt-4 w-full bg-gray-200 rounded-full h-1">
                    <div 
                      className="bg-orange-500 h-1 rounded-full transition-all duration-100"
                      style={{
                        width: `${((currentSlide + 1) / displayLogos.length) * 100}%`
                      }}
                    ></div>
                  </div>
                )}

                {/* Controls Info */}
                <div className="mt-6 text-center text-gray-600 text-sm">
                  <p>
                    <i className="ri-information-line mr-1"></i>
                    Utilisez les flèches ou cliquez sur les miniatures pour naviguer
                    {isPlaying && ' • Défilement automatique activé'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Pourquoi nous font-ils confiance */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Pourquoi nous font-ils confiance ?
              </h2>
              <p className="text-lg text-gray-600">
                Notre expertise reconnue dans la promotion de la culture africaine
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-award-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Excellence Artistique</h3>
                <p className="text-gray-600">
                  Une programmation de qualité qui met en valeur la richesse de la culture africaine
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Rayonnement International</h3>
                <p className="text-gray-600">
                  Des événements qui dépassent les frontières et créent des ponts culturels
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-team-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Engagement Social</h3>
                <p className="text-gray-600">
                  Une mission de cohésion sociale et de promotion de la diversité
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-lightbulb-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Innovation Culturelle</h3>
                <p className="text-gray-600">
                  Des formats originaux qui renouvellent l'approche de la culture africaine
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
